import { datadogLogs } from '@datadog/browser-logs';
import { useEffect } from 'react';

/**
 * Base parameters shared by all workflow types
 */
interface BaseZeroProofsParams {
  /** Entry ID to check for zero proofs */
  entryId: string | undefined;
  /** Whether proofs fetch completed successfully (not IDLE, LOADING, or ERROR) */
  isProofsSuccess: boolean;
  /** Current count of proofs for the entry */
  proofsLength: number;
  /** Folder ID for logging context (waits until available) */
  folderId: string | undefined;
}

/**
 * Parameters for fraud detection workflow
 */
interface FraudDetectionParams extends BaseZeroProofsParams {
  workflow: 'fraud_detection';
  /** The actively assigned entry ID - only logs if viewing the assigned entry */
  assignedEntryId: string | undefined;
}

/**
 * Parameters for income verification workflow
 */
interface IncomeVerificationParams extends BaseZeroProofsParams {
  workflow: 'income_verification';
  /** Income verification review ID - required for IV workflow logging */
  incomeVerificationReviewId: string | undefined;
}

type UseZeroProofsLoggingParams = FraudDetectionParams | IncomeVerificationParams;

/**
 * Safety net hook for detecting and logging zero-proofs scenarios (ITL-2297)
 *
 * This hook logs when a Fraud Analyst would see a blank screen due to missing
 * document proofs. It only logs when ALL conditions are met to prevent false positives:
 *
 * Common conditions (both workflows):
 * - entryId exists (component has an entry to check)
 * - isProofsSuccess is true (proofs fetch completed successfully)
 * - proofsLength === 0 (confirmed zero proofs after successful fetch)
 * - folderId exists (ensures log payload has complete context)
 *
 * Fraud Detection workflow additional condition:
 * - assignedEntryId === entryId (only logs for the actively assigned entry)
 *
 * Income Verification workflow additional condition:
 * - incomeVerificationReviewId exists (required for IV workflow context)
 *
 * @example
 * // Fraud Detection workflow
 * useZeroProofsLogging({
 *   workflow: 'fraud_detection',
 *   entryId: selectedEntryId,
 *   isProofsSuccess,
 *   proofsLength: proofs.length,
 *   folderId: entry?.folder_id,
 *   assignedEntryId: assignedEntry?.id
 * });
 *
 * @example
 * // Income Verification workflow
 * useZeroProofsLogging({
 *   workflow: 'income_verification',
 *   entryId,
 *   isProofsSuccess,
 *   proofsLength: proofs.length,
 *   folderId: entry?.folder_id,
 *   incomeVerificationReviewId
 * });
 */
export const useZeroProofsLogging = (params: UseZeroProofsLoggingParams): void => {
  const { entryId, isProofsSuccess, proofsLength, folderId, workflow } = params;

  // Extract workflow-specific values with stable references.
  // These are derived from params which is already in scope, but extracting them
  // ensures stable dependency array values and cleaner destructuring.
  const assignedEntryId =
    params.workflow === 'fraud_detection' ? params.assignedEntryId : undefined;
  const incomeVerificationReviewId =
    params.workflow === 'income_verification' ? params.incomeVerificationReviewId : undefined;

  useEffect(() => {
    // Common conditions that must be met for all workflows
    const hasCommonConditions = entryId && isProofsSuccess && proofsLength === 0 && folderId;

    if (!hasCommonConditions) {
      return;
    }

    // Build log payload based on workflow
    if (workflow === 'fraud_detection') {
      // Only log for the actively assigned entry, not entries being browsed
      if (assignedEntryId !== entryId) {
        return;
      }

      // Note: If DataDog RUM is enabled, trace_id/span_id are auto-injected.
      // Currently using browser-logs only - correlation with backend traces
      // is available via entry_id/folder_id searchable attributes.
      datadogLogs.logger.error('[Safety Net] FA blank screen - zero proofs detected', {
        entry_id: entryId,
        folder_id: folderId,
        workflow: 'fraud_detection'
      });
    } else {
      // IV workflow requires the review ID for context
      if (!incomeVerificationReviewId) {
        return;
      }

      datadogLogs.logger.error('[Safety Net] FA blank screen - zero proofs detected', {
        entry_id: entryId,
        folder_id: folderId,
        income_verification_review_id: incomeVerificationReviewId,
        workflow: 'income_verification'
      });
    }
  }, [
    entryId,
    isProofsSuccess,
    proofsLength,
    folderId,
    workflow,
    assignedEntryId,
    incomeVerificationReviewId
  ]);
};
